import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { ChatbotProvider } from './context/ChatbotContext';
import MainLayout from './layouts/MainLayout';
import ScrollToTop from './components/layout/ScrollToTop';

// Lazy loaded pages
const HomePage = React.lazy(() => import('./pages/HomePage'));
const ServicesPage = React.lazy(() => import('./pages/ServicesPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const FaqPage = React.lazy(() => import('./pages/FaqPage'));
const BusinessCardPage = React.lazy(() => import('./pages/BusinessCardPage'));
const SuccessPage = React.lazy(() => import('./pages/SuccessPage'));
const CancelPage = React.lazy(() => import('./pages/CancelPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const SitemapPage = React.lazy(() => import('./pages/SitemapPage'));
const LegalPage = React.lazy(() => import('./pages/LegalPage'));

// Loading component
const PageLoading = () => (
  <div className="flex min-h-[100dvh] items-center justify-center px-4 pt-[max(5rem,env(safe-area-inset-top,0px))]">
    <div
      className="h-14 w-14 animate-spin rounded-full border-2 border-primary border-t-transparent"
      aria-hidden
    />
  </div>
);

// Language redirect component
const LanguageRedirect = () => {
  const { pathname } = useLocation();
  const userLang = localStorage.getItem('language') || navigator.language.split('-')[0];
  const targetLang = ['fr', 'en'].includes(userLang) ? userLang : 'en';
  return <Navigate to={`/${targetLang}${pathname}`} replace />;
};

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <LanguageProvider>
        <ChatbotProvider>
          <Suspense fallback={<PageLoading />}>
            <Routes>
              {/* Root redirect */}
              <Route path="/" element={<LanguageRedirect />} />
              
              {/* Language-specific routes */}
              <Route path="/:lang" element={<MainLayout />}>
                <Route index element={<HomePage />} />
                <Route path="services" element={<ServicesPage />} />
                <Route path="pricing" element={<PricingPage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="faq" element={<FaqPage />} />
                <Route path="business-card" element={<BusinessCardPage />} />
                <Route path="sitemap" element={<SitemapPage />} />
                <Route path="legal/:section" element={<LegalPage />} />
                <Route path="success" element={<SuccessPage />} />
                <Route path="cancel" element={<CancelPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </Suspense>
        </ChatbotProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;